import { io } from 'socket.io-client';
import Cookies from 'js-cookie';
import { toast } from '@/components/ui/use-toast';

class WebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.listeners = new Map();
  }

  connect() {
    if (this.socket && this.isConnected) {
      return;
    }

    const token = Cookies.get('token');
    if (!token) {
      console.warn('No token found, cannot connect to WebSocket');
      return;
    }

    const serverUrl = import.meta.env.VITE_BACKEND || 'http://localhost:3000';
    
    this.socket = io(serverUrl, {
      auth: {
        token: token
      },
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: this.reconnectDelay
    });

    this.socket.on('connect', () => {
      console.log('Connected to WebSocket server');
      this.isConnected = true;
      this.reconnectAttempts = 0;
      
      // Show connection success toast
      toast({
        title: "Connected",
        description: "Real-time notifications enabled",
        variant: "default"
      });
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Disconnected from WebSocket server:', reason);
      this.isConnected = false;
      
      if (reason === 'io server disconnect') {
        // Server disconnected, need to reconnect manually
        this.reconnect();
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
      this.isConnected = false;
      
      // Show connection error toast
      toast({
        title: "Connection Error",
        description: "Unable to connect to real-time notifications",
        variant: "destructive"
      });
    });

    // Task-related event listeners
    this.socket.on('task:notification', (notification) => {
      console.log('Task notification received:', notification);
      this.handleTaskNotification(notification);
    });

    this.socket.on('task:updated', (task) => {
      console.log('Task updated:', task);
      this.handleTaskUpdate(task);
    });

    this.socket.on('task:status_updated', (task) => {
      console.log('Task status updated:', task);
      this.handleTaskStatusUpdate(task);
    });

    this.socket.on('notification:read', (data) => {
      console.log('Notification read:', data);
      this.handleNotificationRead(data);
    });

    // Restore custom listeners
    this.listeners.forEach((callback, event) => {
      this.socket.on(event, callback);
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      console.log('Disconnected from WebSocket server');
    }
  }

  reconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(`Reconnecting... Attempt ${this.reconnectAttempts}`);
      
      setTimeout(() => {
        this.connect();
      }, this.reconnectDelay * this.reconnectAttempts);
    } else {
      console.error('Max reconnection attempts reached');
      toast({
        title: "Connection Failed",
        description: "Unable to restore real-time connection",
        variant: "destructive"
      });
    }
  }

  handleTaskNotification(notification) {
    // Show notification toast
    const notificationTypes = {
      ASSIGNED: 'New task assigned',
      REMINDER: 'Task reminder',
      OVERDUE: 'Task overdue',
      COMPLETED: 'Task completed'
    };

    toast({
      title: notificationTypes[notification.type] || 'Task notification',
      description: notification.task.title,
      variant: notification.type === 'OVERDUE' ? 'destructive' : 'default'
    });

    // Dispatch custom event for components to listen to
    window.dispatchEvent(new CustomEvent('task:notification', {
      detail: notification
    }));
  }

  handleTaskUpdate(task) {
    // Dispatch custom event for components to listen to
    window.dispatchEvent(new CustomEvent('task:updated', {
      detail: task
    }));
  }

  handleTaskStatusUpdate(task) {
    // Dispatch custom event for components to listen to
    window.dispatchEvent(new CustomEvent('task:status_updated', {
      detail: task
    }));
  }

  handleNotificationRead(data) {
    // Dispatch custom event for components to listen to
    window.dispatchEvent(new CustomEvent('notification:read', {
      detail: data
    }));
  }

  // Allow components to listen to custom events
  on(event, callback) {
    this.listeners.set(event, callback);
    if (this.socket) {
      this.socket.on(event, callback);
    }
  }

  off(event) {
    this.listeners.delete(event);
    if (this.socket) {
      this.socket.off(event);
    }
  }

  // Send events to server
  emit(event, data) {
    if (this.socket && this.isConnected) {
      this.socket.emit(event, data);
    }
  }

  // Update task status
  updateTaskStatus(taskId, status) {
    this.emit('task:status_update', { taskId, status });
  }

  // Mark notification as read
  markNotificationAsRead(notificationId) {
    this.emit('notification:read', { notificationId });
  }

  getConnectionStatus() {
    return this.isConnected;
  }
}

// Create singleton instance
const webSocketService = new WebSocketService();

export default webSocketService;